import { Globe } from "lucide-react";
import { useState } from "react";

import { CitationsModal } from "./CitationsModal";
import { IMessageStore } from "./Playground/Messages/Message/message-store";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ContainerFileAnnotation,
  FileAnnotation,
  MapAnnotation,
  Message,
  TextContent,
  WebAnnotation,
} from "@/types/prompt-blueprint";

type Annotation = WebAnnotation | FileAnnotation | MapAnnotation | ContainerFileAnnotation;

type UnifiedMessage = IMessageStore | Message;

interface CitationsProps {
  messages: UnifiedMessage[];
  /** Tighter spacing for inline use (e.g. chat bubbles) */
  compact?: boolean;
}

const isIMessageStore = (message: UnifiedMessage): message is IMessageStore =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  "rawContent" in message && typeof (message as any).rawContent === "object";

const getMessageContent = (message: UnifiedMessage): TextContent[] => {
  if (isIMessageStore(message)) {
    const { rawContent } = message;
    if (!rawContent) {
      return [];
    }
    return rawContent.filter((content): content is TextContent => content.type === "text");
  }
  return Array.isArray(message.content)
    ? message.content.filter((content): content is TextContent => content.type === "text")
    : [];
};

export const Citations = ({ messages, compact = false }: CitationsProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const allAnnotations = messages.reduce<Annotation[]>((acc, message) => {
    const textContent = getMessageContent(message);

    textContent.forEach(content => {
      if (content.annotations) {
        acc.push(...content.annotations);
      }
    });

    return acc;
  }, []);

  const fileCitations = allAnnotations
    .filter((annotation): annotation is FileAnnotation => annotation.type === "file_citation")
    .filter(
      (citation, index, self) => index === self.findIndex(c => c.file_id === citation.file_id),
    );

  const webCitations = allAnnotations.filter(
    (annotation): annotation is WebAnnotation => annotation.type === "url_citation",
  );

  const mapCitations = allAnnotations.filter(
    (annotation): annotation is MapAnnotation => annotation.type === "map_citation",
  );

  const containerFileCitations = allAnnotations.filter(
    (annotation): annotation is ContainerFileAnnotation =>
      annotation.type === "container_file_citation",
  );

  const totalSources =
    fileCitations.length +
    webCitations.length +
    mapCitations.length +
    containerFileCitations.length;

  if (totalSources === 0) {
    return null;
  }

  return (
    <>
      <div className={cn("relative z-10 flex justify-start", compact ? "mt-0" : "mt-2")}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsModalOpen(true)}
          className="relative z-10 flex items-center gap-2 rounded-full border-blue-200 bg-blue-50 px-3 py-1 text-xs text-blue-700 hover:bg-blue-100"
        >
          <Globe className="h-3 w-3" />
          {totalSources} source{totalSources !== 1 ? "s" : ""}
        </Button>
      </div>

      <CitationsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fileCitations={fileCitations}
        webCitations={webCitations}
        mapCitations={mapCitations}
        containerFileCitations={containerFileCitations}
      />
    </>
  );
};
